// Components
import { Select, Group, Stack } from "@mantine/core";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import { FormActions } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import { PRACTITIONER_SPECIALTY_SELECT_OPTIONS } from "@benchmark/core/constants";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { useCallback } from "react";
import { useFormSubmission } from "@benchmark/ui/hooks";

// Types
import { PractitionerFormData } from "@benchmark/core/types";
import type { Practitioner, PractitionerRole } from "@medplum/fhirtypes";

// Utils
import { formValidators } from "@benchmark/ui/utils";
import { showSuccessNotification } from "@benchmark/ui/utils";
import { toE164US } from "@benchmark/core/utils";
import { createPractitionerFacade } from "@/facades/practitioner.facade";

/**
 * FHIR-Compliant Practitioner Form
 *
 * This form maintains FHIR R4 compliance through several key measures:
 *
 * 1. PRACTITIONER RESOURCE COMPLIANCE:
 *    - Form data maps directly to FHIR Practitioner resource structure
 *    - Name components (given, family) properly structured per FHIR HumanName
 *    - Telecom information follows FHIR ContactPoint specifications
 *    - Active status and resource metadata properly maintained
 *
 * 2. PRACTITIONER ROLE COMPLIANCE:
 *    - Specialty coding uses FHIR R4 Practice Setting Code Value Set
 *    - Proper SNOMED CT system URLs and codes maintained
 *    - PractitionerRole resource links correctly to Practitioner
 *    - Telecom duplication ensures accessibility through both resources
 *
 * 3. VALIDATION & DATA INTEGRITY:
 *    - Required fields enforced per FHIR Practitioner requirements
 *    - Email and phone validation ensures proper ContactPoint format
 *    - Specialty validation against approved FHIR value sets
 *    - Input sanitization prevents invalid FHIR resource creation
 *
 * 4. RESOURCE RELATIONSHIP COMPLIANCE:
 *    - Proper Reference structure between Practitioner and PractitionerRole
 *    - Consistent identifier and reference management
 *    - Maintains referential integrity for appointment assignments
 *
 * The form validation and data conversion logic in Practitioner.utils.ts ensures
 * that all data is properly structured according to FHIR R4 specifications
 * before being sent to the FHIR server.
 * @agentic connections
 */

/**
 * Props for the practitioner form.
 * @agentic connections
 */
type PractitionerFormProps = {
  onPractitionerCreated: (
    practitioner: Practitioner,
    practitionerRole: PractitionerRole,
  ) => void;
  onComplete?: () => void;
};

/**
 * Form for creating FHIR Practitioner and PractitionerRole.
 * @agentic connections
 */
export const PractitionerForm = (props: PractitionerFormProps) => {
  const { onPractitionerCreated, onComplete } = props;
  const medplum = useMedplum();
  const practitionerFacade = createPractitionerFacade(medplum);

  // Form state management with Mantine
  const form = useForm<PractitionerFormData>({
    initialValues: {
      "practitioner-first-name": "",
      "practitioner-last-name": "",
      "practitioner-email": "",
      "practitioner-phone": "",
      "practitioner-specialty": "",
    },
    validate: {
      "practitioner-first-name": formValidators.required("First name"),
      "practitioner-last-name": formValidators.required("Last name"),
      "practitioner-specialty": formValidators.required("Specialty"),
      "practitioner-email": formValidators.email(false),
      "practitioner-phone": formValidators.phone(false),
    },
  });

  // Use shared form submission handling
  const { isSubmitting, createSubmissionHandler } = useFormSubmission();

  const handleSubmit = useCallback(
    (formData: PractitionerFormData) => {
      return createSubmissionHandler(async (formData: PractitionerFormData) => {
        // Normalize phone to E.164 (US) if present
        if (formData["practitioner-phone"]) {
          formData["practitioner-phone"] = toE164US(
            formData["practitioner-phone"],
          );
        }
        const { practitioner, practitionerRole } =
          await practitionerFacade.create(formData);

        showSuccessNotification("Practitioner created successfully!");

        onPractitionerCreated(practitioner, practitionerRole);
        onComplete?.();
      })(formData);
    },
    [
      practitionerFacade,
      onPractitionerCreated,
      onComplete,
      createSubmissionHandler,
    ],
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group grow>
          <TextInput
            label="First Name"
            placeholder="Enter first name"
            required
            {...form.getInputProps("practitioner-first-name")}
          />

          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            required
            {...form.getInputProps("practitioner-last-name")}
          />
        </Group>

        <Group grow>
          <TextInput
            label="Email"
            placeholder="Enter email address"
            type="email"
            {...form.getInputProps("practitioner-email")}
          />

          <PhoneInput
            label="Phone"
            placeholder="Enter phone number"
            {...form.getInputProps("practitioner-phone")}
          />
        </Group>

        <Select
          label="Specialty"
          placeholder="Select specialty"
          data={PRACTITIONER_SPECIALTY_SELECT_OPTIONS}
          searchable
          clearable
          {...form.getInputProps("practitioner-specialty")}
        />

        <FormActions
          isSubmitting={isSubmitting}
          resourceType="Practitioner"
          isEditing={false}
          showCancel={false}
        />
      </Stack>
    </form>
  );
};
